import React, { useMemo, useEffect, useState } from 'react'; // Importamos React y useMemo para valores derivados
import { IonButton, IonContent, IonPage, useIonToast } from '@ionic/react'; // Importamos componentes de Ionic para la vista y toasts
import Header from 'src/components/Header'; // Reutilizamos el Header del proyecto para mantener estilos
import { useHistory } from 'react-router'; // Importamos useHistory para navegación interna
import AcompanamientoAPI from 'src/services/acompanamiento'; // Servicio HTTP del backend para listar acompañamientos

// Tipo local para las props mínimas que vamos a usar en esta vista
 type Props = { // Definimos las props del componente
   user: string; // Nombre del usuario autenticado (Fiscal), suministrado por App.tsx
   cargo?: string; // Cargo del usuario para posibles reglas de visibilidad
 };

// Componente principal de la página de Acompañamiento
 const Acompanamiento: React.FC<Props> = ({ user, cargo }) => { // Declaramos el componente funcional con sus props
   const [present] = useIonToast(); // Hook de Ionic para mostrar toasts
   const history = useHistory(); // Obtenemos history del router para navegar

   // Estado: registros desde backend
   const [registros, setRegistros] = useState<Array<any>>([]); // Lista de acompañamientos

   // Fecha formateada de hoy para el encabezado (se recalcula solo al montar)
   const hoy = useMemo(() => { // Memoizamos el valor para evitar recalcular en cada render
     const d = new Date(); // Obtenemos la fecha actual
     const pad = (n: number) => String(n).padStart(2, '0'); // Helper para completar con ceros
     return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`; // Devolvemos fecha en formato DD/MM/YYYY HH:mm
   }, []); // Dependencias vacías para calcular una vez

   // Acción del botón "Nuevo": navegamos al formulario
   const onNuevo = () => { // Manejador del click en el botón Nuevo
     history.push('/acompanamiento/nuevo'); // Navegación sin recarga a la ruta del formulario
   }; // Cerramos la función onNuevo

   // Cargar registros desde backend al montar
   useEffect(() => {
     (async () => {
       try {
         const { data } = await AcompanamientoAPI.listar({ limit: 50, offset: 0 }); // Traemos últimos registros
         const lista = Array.isArray(data?.datos) ? data.datos : [];
         setRegistros(lista);
       } catch (e: any) {
         present({ message: e?.response?.data?.error || 'No se pudo listar los acompañamientos.', duration: 3000, color: 'warning' });
         setRegistros([]);
       }
     })();
   }, [present]);

   return ( // Comenzamos el JSX de la página
     <IonPage> {/* Contenedor de página de Ionic */}
       <Header titulo="Acompañamiento" /> {/* Título del módulo */}

       <IonContent id="acompanamiento" className="ion-padding"> {/* Contenido con padding e id único */}
         <div className="container mt-1"> {/* Contenedor principal alineado al estilo del proyecto */}
           <div className="d-flex justify-content-between align-items-start"> {/* Fila superior con datos y botón */}
             <div style={{ maxWidth: '70%' }}> {/* Columna izquierda con los datos del fiscal y fecha */}
               <div className="card" style={{ padding: 12 }}> {/* Caja simple para simular el bloque del mockup */}
                 <p className="m-0">{user || '—'} {/* Mostramos el nombre del usuario */}</p>
                 <p className="m-0"><strong>Fecha:</strong> {hoy} {/* Mostramos la fecha actual */}</p>
                 {/* Cargo oculto a pedido: si se requiere, reactivar esta línea más adelante */}
               </div>
             </div>

             <div> {/* Columna derecha con el botón Nuevo */}
               <IonButton color="primary" onClick={onNuevo}> {/* Botón para crear nuevo registro (placeholder) */}
                 Nuevo {/* Texto del botón */}
               </IonButton>
             </div>
           </div>

           <div className="mt-2"> {/* Separación vertical antes del listado */}
             <div className="card" style={{ padding: 12 }}> {/* Contenedor del historial, simulando el marco grande */}
               <div className="d-flex px-2" style={{ fontWeight: 600 }}> {/* Encabezado de columnas */}
                 <div style={{ flex: 2 }}>Punto de Servicio</div> {/* Columna PS */}
                 <div style={{ flex: 1 }}>Fecha</div> {/* Columna Fecha */}
                 <div style={{ flex: 1 }}>Puntaje</div> {/* Columna Puntaje */}
               </div>
               <hr className="my-2" /> {/* Separador visual */}

               {registros.length === 0 && ( // Si no hay registros, mostramos mensaje vacío
                 <div className="px-2 py-3 text-muted"> {/* Contenedor del mensaje vacío */}
                   Aún no existen registros de acompañamiento. {/* Texto informativo */}
                 </div>
               )}

               {registros.map((r: any, idx: number) => ( // Renderizamos cada registro desde backend
                 <div key={idx} className="d-flex px-2 py-1" style={{ borderTop: '1px solid rgba(0,0,0,0.05)' }}> {/* Fila del registro */}
                   <div style={{ flex: 2 }}>{r?.ps_nombre ?? '—'}</div> {/* Punto de Servicio */}
                   <div style={{ flex: 1 }}>{r?.fecha_visita ? new Date(r.fecha_visita).toLocaleString() : '—'}</div> {/* Fecha */}
                   <div style={{ flex: 1 }}>{r?.puntaje_total != null ? r.puntaje_total : '—'}</div> {/* Puntaje */}
                 </div>
               ))}
             </div>
           </div>
         </div>
       </IonContent>
     </IonPage>
   ); // Cerramos el return
 }; // Cerramos el componente

 export default Acompanamiento; // Exportamos el componente directamente porque no necesitamos withRouter
